import { command, waitForNextFrame } from '@app-actions/command-palette-action'
import { useCommandPaletteActions } from '@app-actions/command-palette-store'
import { getAuthHeaders } from '@context-providers/auth-context-provider'
import { useNavigate } from '@tanstack/react-router'

import { useEffect } from 'react'

import { APP_CONFIG } from '@lib/config'
import { downloadBlob, getFilenameFromContentDisposition } from '@lib/excel-download'

export const useAdminGlobalActions = () => {
  const navigate = useNavigate()
  const { registerGlobalActions } = useCommandPaletteActions()

  useEffect(() => {
    const downloadFromExcelIo = async (endpoint: string, fallbackFilename: string) => {
      const response = await fetch(`${APP_CONFIG.apiURL}/excel-io/${endpoint}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }

      const blob = await response.blob()
      const filename = getFilenameFromContentDisposition(response.headers.get('Content-Disposition'), fallbackFilename)
      downloadBlob(blob, filename)
    }

    const actions = [
      command({
        key: 'navigate-admin-overview',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Overview',
        keywords: ['admin', 'overview'],
        run: async () => {
          await navigate({ to: '/admin' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-dropdown',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new DropDown',
        keywords: ['create', 'new', 'dropdown', 'dropdown'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'DropDown')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'DropDown' } }))
          }
          await navigate({ to: '/admin/dropDown' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-dropdownelement',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new DropDownElement',
        keywords: ['create', 'new', 'dropdownelement', 'dropdownelement'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'DropDownElement')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'DropDownElement' } }))
          }
          await navigate({ to: '/admin/dropDownElement' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-hierarchy',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Hierarchy',
        keywords: ['create', 'new', 'hierarchy', 'hierarchy'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Hierarchy')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Hierarchy' } }))
          }
          await navigate({ to: '/admin/hierarchy' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-hierarchynode',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new HierarchyNode',
        keywords: ['create', 'new', 'hierarchynode', 'hierarchynode'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'HierarchyNode')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'HierarchyNode' } }))
          }
          await navigate({ to: '/admin/hierarchyNode' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-organization',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Organization',
        keywords: ['create', 'new', 'organization', 'organization'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Organization')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Organization' } }))
          }
          await navigate({ to: '/admin/organization' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-period',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Period',
        keywords: ['create', 'new', 'period', 'period'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Period')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Period' } }))
          }
          await navigate({ to: '/admin/period' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-periodconfiguration',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new PeriodConfiguration',
        keywords: ['create', 'new', 'periodconfiguration', 'periodconfiguration'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'PeriodConfiguration')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'PeriodConfiguration' } }))
          }
          await navigate({ to: '/admin/periodConfiguration' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-person',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Person',
        keywords: ['create', 'new', 'person', 'person'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Person')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Person' } }))
          }
          await navigate({ to: '/admin/person' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-personhierarchynode',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new PersonHierarchyNode',
        keywords: ['create', 'new', 'personhierarchynode', 'personhierarchynode'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'PersonHierarchyNode')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'PersonHierarchyNode' } }))
          }
          await navigate({ to: '/admin/personHierarchyNode' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-pnlrow',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new PnlRow',
        keywords: ['create', 'new', 'pnlrow', 'pnlrow'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'PnlRow')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'PnlRow' } }))
          }
          await navigate({ to: '/admin/pnlRow' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-pnlstructure',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new PnlStructure',
        keywords: ['create', 'new', 'pnlstructure', 'pnlstructure'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'PnlStructure')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'PnlStructure' } }))
          }
          await navigate({ to: '/admin/pnlStructure' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-program',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Program',
        keywords: ['create', 'new', 'program', 'program'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Program')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Program' } }))
          }
          await navigate({ to: '/admin/program' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-programlabel',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new ProgramLabel',
        keywords: ['create', 'new', 'programlabel', 'programlabel'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'ProgramLabel')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'ProgramLabel' } }))
          }
          await navigate({ to: '/admin/programLabel' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-userprogram',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new UserProgram',
        keywords: ['create', 'new', 'userprogram', 'userprogram'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'UserProgram')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'UserProgram' } }))
          }
          await navigate({ to: '/admin/userProgram' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-deliverable',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Deliverable',
        keywords: ['create', 'new', 'deliverable', 'deliverable'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Deliverable')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Deliverable' } }))
          }
          await navigate({ to: '/admin/deliverable' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-deliverableoneoffcostitem',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new DeliverableOneOffCostItem',
        keywords: ['create', 'new', 'deliverableoneoffcostitem', 'deliverableoneoffcostitem'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'DeliverableOneOffCostItem')
            globalThis.dispatchEvent(
              new CustomEvent('pxl:create-intent', { detail: { model: 'DeliverableOneOffCostItem' } }),
            )
          }
          await navigate({ to: '/admin/deliverableOneOffCostItem' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-oneoffcost',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new OneOffCost',
        keywords: ['create', 'new', 'oneoffcost', 'oneoffcost'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'OneOffCost')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'OneOffCost' } }))
          }
          await navigate({ to: '/admin/oneOffCost' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-pnlvalue',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new PnlValue',
        keywords: ['create', 'new', 'pnlvalue', 'pnlvalue'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'PnlValue')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'PnlValue' } }))
          }
          await navigate({ to: '/admin/pnlValue' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-comment',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new Comment',
        keywords: ['create', 'new', 'comment', 'comment'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'Comment')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'Comment' } }))
          }
          await navigate({ to: '/admin/comment' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-file',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new File',
        keywords: ['create', 'new', 'file', 'file'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'File')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'File' } }))
          }
          await navigate({ to: '/admin/file' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-tableview',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new TableView',
        keywords: ['create', 'new', 'tableview', 'tableview'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'TableView')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'TableView' } }))
          }
          await navigate({ to: '/admin/tableView' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'create-admin-user',
        visibility: 'visible',
        group: 'Create',
        label: 'Create new User',
        keywords: ['create', 'new', 'user', 'user'],
        run: async () => {
          if (typeof globalThis !== 'undefined') {
            globalThis.sessionStorage.setItem('pxl:create-intent-model', 'User')
            globalThis.dispatchEvent(new CustomEvent('pxl:create-intent', { detail: { model: 'User' } }))
          }
          await navigate({ to: '/admin/user' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-dropdown',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: DropDown',
        keywords: ['admin', 'dropdown', 'dropdown'],
        run: async () => {
          await navigate({ to: '/admin/dropDown' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-dropdownelement',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: DropDownElement',
        keywords: ['admin', 'dropdownelement', 'dropdownelement'],
        run: async () => {
          await navigate({ to: '/admin/dropDownElement' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-hierarchy',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Hierarchy',
        keywords: ['admin', 'hierarchy', 'hierarchy'],
        run: async () => {
          await navigate({ to: '/admin/hierarchy' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-hierarchynode',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: HierarchyNode',
        keywords: ['admin', 'hierarchynode', 'hierarchynode'],
        run: async () => {
          await navigate({ to: '/admin/hierarchyNode' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-organization',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Organization',
        keywords: ['admin', 'organization', 'organization'],
        run: async () => {
          await navigate({ to: '/admin/organization' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-period',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Period',
        keywords: ['admin', 'period', 'period'],
        run: async () => {
          await navigate({ to: '/admin/period' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-periodconfiguration',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: PeriodConfiguration',
        keywords: ['admin', 'periodconfiguration', 'periodconfiguration'],
        run: async () => {
          await navigate({ to: '/admin/periodConfiguration' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-person',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Person',
        keywords: ['admin', 'person', 'person'],
        run: async () => {
          await navigate({ to: '/admin/person' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-personhierarchynode',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: PersonHierarchyNode',
        keywords: ['admin', 'personhierarchynode', 'personhierarchynode'],
        run: async () => {
          await navigate({ to: '/admin/personHierarchyNode' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-pnlrow',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: PnlRow',
        keywords: ['admin', 'pnlrow', 'pnlrow'],
        run: async () => {
          await navigate({ to: '/admin/pnlRow' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-pnlstructure',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: PnlStructure',
        keywords: ['admin', 'pnlstructure', 'pnlstructure'],
        run: async () => {
          await navigate({ to: '/admin/pnlStructure' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-program',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Program',
        keywords: ['admin', 'program', 'program'],
        run: async () => {
          await navigate({ to: '/admin/program' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-programlabel',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: ProgramLabel',
        keywords: ['admin', 'programlabel', 'programlabel'],
        run: async () => {
          await navigate({ to: '/admin/programLabel' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-userprogram',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: UserProgram',
        keywords: ['admin', 'userprogram', 'userprogram'],
        run: async () => {
          await navigate({ to: '/admin/userProgram' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-deliverable',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Deliverable',
        keywords: ['admin', 'deliverable', 'deliverable'],
        run: async () => {
          await navigate({ to: '/admin/deliverable' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-deliverableoneoffcostitem',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: DeliverableOneOffCostItem',
        keywords: ['admin', 'deliverableoneoffcostitem', 'deliverableoneoffcostitem'],
        run: async () => {
          await navigate({ to: '/admin/deliverableOneOffCostItem' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-oneoffcost',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: OneOffCost',
        keywords: ['admin', 'oneoffcost', 'oneoffcost'],
        run: async () => {
          await navigate({ to: '/admin/oneOffCost' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-pnlvalue',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: PnlValue',
        keywords: ['admin', 'pnlvalue', 'pnlvalue'],
        run: async () => {
          await navigate({ to: '/admin/pnlValue' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-action',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Action',
        keywords: ['admin', 'action', 'action'],
        run: async () => {
          await navigate({ to: '/admin/action' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-actionoperation',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: ActionOperation',
        keywords: ['admin', 'actionoperation', 'actionoperation'],
        run: async () => {
          await navigate({ to: '/admin/actionOperation' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-comment',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: Comment',
        keywords: ['admin', 'comment', 'comment'],
        run: async () => {
          await navigate({ to: '/admin/comment' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-file',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: File',
        keywords: ['admin', 'file', 'file'],
        run: async () => {
          await navigate({ to: '/admin/file' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-tableview',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: TableView',
        keywords: ['admin', 'tableview', 'tableview'],
        run: async () => {
          await navigate({ to: '/admin/tableView' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),

      command({
        key: 'navigate-admin-user',
        visibility: 'visible',
        group: 'Navigate',
        label: 'Admin: User',
        keywords: ['admin', 'user', 'user'],
        run: async () => {
          await navigate({ to: '/admin/user' })
          await waitForNextFrame()
          return { kind: 'close' }
        },
      }),
    ]

    if (import.meta.env.VITE_PUBLIC_ALLOW_ADMIN === 'true') {
      actions.push(
        command({
          key: 'navigate-admin-data-management',
          visibility: 'visible',
          group: 'Navigate',
          label: 'Admin: Data Management',
          keywords: ['admin', 'data', 'management'],
          run: async () => {
            await navigate({ to: '/admin/data-management' })
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'navigate-admin-excel-io',
          visibility: 'visible',
          group: 'Navigate',
          label: 'Admin: Excel I/O',
          keywords: ['admin', 'import', 'export', 'excel'],
          run: async () => {
            await navigate({ to: '/admin/excel-io' })
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-all-models-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: All models (Excel)',
          keywords: ['export', 'excel', 'all', 'models'],
          run: async () => {
            await downloadFromExcelIo('export/all', 'all-models.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),

        command({
          key: 'export-admin-dropdown-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: DropDown (Excel)',
          keywords: ['export', 'excel', 'dropdown', 'dropdown'],
          run: async () => {
            await downloadFromExcelIo('export/dropDown', 'dropDown.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-dropdownelement-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: DropDownElement (Excel)',
          keywords: ['export', 'excel', 'dropdownelement', 'dropdownelement'],
          run: async () => {
            await downloadFromExcelIo('export/dropDownElement', 'dropDownElement.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-hierarchy-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Hierarchy (Excel)',
          keywords: ['export', 'excel', 'hierarchy', 'hierarchy'],
          run: async () => {
            await downloadFromExcelIo('export/hierarchy', 'hierarchy.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-hierarchynode-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: HierarchyNode (Excel)',
          keywords: ['export', 'excel', 'hierarchynode', 'hierarchynode'],
          run: async () => {
            await downloadFromExcelIo('export/hierarchyNode', 'hierarchyNode.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-organization-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Organization (Excel)',
          keywords: ['export', 'excel', 'organization', 'organization'],
          run: async () => {
            await downloadFromExcelIo('export/organization', 'organization.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-period-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Period (Excel)',
          keywords: ['export', 'excel', 'period', 'period'],
          run: async () => {
            await downloadFromExcelIo('export/period', 'period.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-periodconfiguration-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: PeriodConfiguration (Excel)',
          keywords: ['export', 'excel', 'periodconfiguration', 'periodconfiguration'],
          run: async () => {
            await downloadFromExcelIo('export/periodConfiguration', 'periodConfiguration.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-person-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Person (Excel)',
          keywords: ['export', 'excel', 'person', 'person'],
          run: async () => {
            await downloadFromExcelIo('export/person', 'person.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-personhierarchynode-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: PersonHierarchyNode (Excel)',
          keywords: ['export', 'excel', 'personhierarchynode', 'personhierarchynode'],
          run: async () => {
            await downloadFromExcelIo('export/personHierarchyNode', 'personHierarchyNode.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-pnlrow-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: PnlRow (Excel)',
          keywords: ['export', 'excel', 'pnlrow', 'pnlrow'],
          run: async () => {
            await downloadFromExcelIo('export/pnlRow', 'pnlRow.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-pnlstructure-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: PnlStructure (Excel)',
          keywords: ['export', 'excel', 'pnlstructure', 'pnlstructure'],
          run: async () => {
            await downloadFromExcelIo('export/pnlStructure', 'pnlStructure.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-program-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Program (Excel)',
          keywords: ['export', 'excel', 'program', 'program'],
          run: async () => {
            await downloadFromExcelIo('export/program', 'program.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-programlabel-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: ProgramLabel (Excel)',
          keywords: ['export', 'excel', 'programlabel', 'programlabel'],
          run: async () => {
            await downloadFromExcelIo('export/programLabel', 'programLabel.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-userprogram-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: UserProgram (Excel)',
          keywords: ['export', 'excel', 'userprogram', 'userprogram'],
          run: async () => {
            await downloadFromExcelIo('export/userProgram', 'userProgram.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-deliverable-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Deliverable (Excel)',
          keywords: ['export', 'excel', 'deliverable', 'deliverable'],
          run: async () => {
            await downloadFromExcelIo('export/deliverable', 'deliverable.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-deliverableoneoffcostitem-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: DeliverableOneOffCostItem (Excel)',
          keywords: ['export', 'excel', 'deliverableoneoffcostitem', 'deliverableoneoffcostitem'],
          run: async () => {
            await downloadFromExcelIo('export/deliverableOneOffCostItem', 'deliverableOneOffCostItem.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-oneoffcost-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: OneOffCost (Excel)',
          keywords: ['export', 'excel', 'oneoffcost', 'oneoffcost'],
          run: async () => {
            await downloadFromExcelIo('export/oneOffCost', 'oneOffCost.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-pnlvalue-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: PnlValue (Excel)',
          keywords: ['export', 'excel', 'pnlvalue', 'pnlvalue'],
          run: async () => {
            await downloadFromExcelIo('export/pnlValue', 'pnlValue.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-action-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Action (Excel)',
          keywords: ['export', 'excel', 'action', 'action'],
          run: async () => {
            await downloadFromExcelIo('export/action', 'action.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-actionoperation-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: ActionOperation (Excel)',
          keywords: ['export', 'excel', 'actionoperation', 'actionoperation'],
          run: async () => {
            await downloadFromExcelIo('export/actionOperation', 'actionOperation.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-comment-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: Comment (Excel)',
          keywords: ['export', 'excel', 'comment', 'comment'],
          run: async () => {
            await downloadFromExcelIo('export/comment', 'comment.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-file-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: File (Excel)',
          keywords: ['export', 'excel', 'file', 'file'],
          run: async () => {
            await downloadFromExcelIo('export/file', 'file.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-tableview-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: TableView (Excel)',
          keywords: ['export', 'excel', 'tableview', 'tableview'],
          run: async () => {
            await downloadFromExcelIo('export/tableView', 'tableView.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
        command({
          key: 'export-admin-user-excel',
          visibility: 'visible',
          group: 'Export',
          label: 'Export: User (Excel)',
          keywords: ['export', 'excel', 'user', 'user'],
          run: async () => {
            await downloadFromExcelIo('export/user', 'user.xlsx')
            await waitForNextFrame()
            return { kind: 'close' }
          },
        }),
      )
    }

    registerGlobalActions(...actions)
  }, [navigate, registerGlobalActions])
}
